'use client'

import * as React from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { defaultContentLanguage, mapLocaleToContentLanguage } from 'cf-i18n/constants'
import { useUserId } from '@repo/cf-user/hooks/use-user-data'

import { useContentFetchReviewsQuery, useContentMyReviewQuery } from '@web/gen/content'
import { Rating } from './rating'
import { RatingDistribution } from './distribution'
import { ReviewCard } from './review-card'
import { MAX_REVIEWS } from '../reviews/reviews'
import { useProductId } from '@web/app/[locale]/(web)/prod/[slug]/product-id-context'

export function RatingSection() {
  const t = useTranslations('prod.product_description_page.reviews')
  const productId = useProductId()
  const locale = useLocale()
  const currentUserId = useUserId()
  const isLoggedIn = Boolean(currentUserId)
  const contentLanguage = mapLocaleToContentLanguage.get(locale) || defaultContentLanguage
  const { data, isPending } = useContentFetchReviewsQuery({
    input: {
      productId: productId ?? '',
      language: contentLanguage,
      limit: MAX_REVIEWS,
    },
  })
  const ratingValue = data?.contentProductReviews.rating.average ?? 0
  const reviewTotal = data?.contentProductReviews.rating.count ?? 0
  const reviewsByStars = React.useMemo(
    () =>
      data?.contentProductReviews.rating.groups.reduce(
        (acc, group) => {
          acc[group.rating] = group.count
          return acc
        },
        {
          1: 0,
          2: 0,
          3: 0,
          4: 0,
          5: 0,
        } as Record<string, number>,
      ) ?? {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
      },
    [data?.contentProductReviews.rating.groups],
  )
  const { data: userReview } = useContentMyReviewQuery(
    {
      productId: productId ?? '',
    },
    {
      enabled: isLoggedIn,
    },
  )
  const currentUserReview = userReview?.contentMyReview
  const isReadOnlyReview = currentUserReview ? isOlderThanAWeek(currentUserReview.createdAt) : false
  const showReviews = reviewTotal > 0 || isPending

  return (
    <>
      {showReviews ? (
        <div className="mb-10 flex flex-wrap items-center gap-4">
          <Rating value={ratingValue} className="h-8" readOnly />
          {ratingValue ? <div className="text-lg font-bold">{ratingValue.toFixed(1)}</div> : null}
          <div className="text-cf-dreamy-cloud-900 w-full grow text-[16px] min-[575px]:w-auto min-[575px]:text-[14px]">
            {t('number', {
              number: reviewTotal,
            }).replace(`${reviewTotal}`, reviewTotal.toLocaleString())}
          </div>
        </div>
      ) : null}
      <div className="flex flex-col items-start justify-start gap-4 min-[575px]:flex-row">
        {showReviews ? (
          <RatingDistribution
            reviewsByStars={reviewsByStars}
            reviewTotal={reviewTotal}
            isLoading={isPending}
          />
        ) : null}
        <ReviewCard
          readOnly={isReadOnlyReview}
          rating={Math.floor(currentUserReview?.rating ?? 0)}
          className="3xl:w-[470px] w-full min-[575px]:w-fit"
        />
      </div>
    </>
  )
}

function isOlderThanAWeek(dateString: string): boolean {
  const inputDate = new Date(dateString)
  const currentDate = new Date()
  const timeDifference = currentDate.getTime() - inputDate.getTime()
  const sevenDaysInMilliseconds = 7 * 24 * 60 * 60 * 1000

  return timeDifference > sevenDaysInMilliseconds
}
